import { type FC, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Circle,
  Sketch,
} from '@uiw/react-color';
import {
  hexToHsva,
  hsvaToHex,
  hsvaToRgbaString,
  rgbaStringToHsva,
  validHex,
} from '@uiw/color-convert';
import { getSchemas } from '../api/schemas';
import { updateConnection } from '../api/connections';
import { Algorithm } from '../edges/EditableEdge/constants';
import {
  DEFAULT_LINE_STROKE_HEX,
  LINE_SHEET_PALETTE,
  LINE_STANDARD_SWATCHES,
} from './shared/sheetLineColorPalette';
import { Droplets, Pipette } from 'lucide-react';

// Smart Line Editing constants

function getEyeDropperCtor():
  | (new () => { open: () => Promise<{ sRGBHex: string }> })
  | undefined {
  if (typeof window === 'undefined') return undefined;
  const w = window as unknown as { EyeDropper?: new () => { open: () => Promise<{ sRGBHex: string }> } };
  return w.EyeDropper;
}

function normalizeCssStrokeToHsva(stroke: string | undefined) {
  const raw = (stroke || DEFAULT_LINE_STROKE_HEX).trim();
  if (/^rgba?\(/i.test(raw)) return rgbaStringToHsva(raw);
  const hexCand = raw.startsWith('#') ? raw : `#${raw}`;
  if (validHex(hexCand)) return hexToHsva(hexCand);
  return hexToHsva(DEFAULT_LINE_STROKE_HEX);
}

const LINE_ALGORITHM_OPTIONS: { value: Algorithm; label: string }[] = [
  { value: Algorithm.Linear, label: 'Прямая' },
  { value: Algorithm.Step, label: 'Г-образная / ступенчатая' },
  { value: Algorithm.CatmullRom, label: 'Кривая Catmull-Rom' },
  { value: Algorithm.BezierCatmullRom, label: 'Кривая Безье' },
];

const LINE_STYLES = {
  solid: { 
    strokeDasharray: '', 
    label: 'Сплошная',
    icon: (
      <svg width="20" height="12" viewBox="0 0 20 12" className="mr-2">
        <line x1="2" y1="6" x2="18" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    )
  },
  dashed: { 
    strokeDasharray: '8 4', 
    label: 'Пунктир',
    icon: (
      <svg width="20" height="12" viewBox="0 0 20 12" className="mr-2">
        <line x1="2" y1="6" x2="18" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 3" />
      </svg>
    )
  },
  dotted: { 
    strokeDasharray: '2 4', 
    label: 'Точки',
    icon: (
      <svg width="20" height="12" viewBox="0 0 20 12" className="mr-2">
        <line x1="2" y1="6" x2="18" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="1 2" />
      </svg>
    )
  },
  dashdot: { 
    strokeDasharray: '10 4 2 4', 
    label: 'Штрих-пунктир',
    icon: (
      <svg width="20" height="12" viewBox="0 0 20 12" className="mr-2">
        <line x1="2" y1="6" x2="18" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="5 2 1 2" />
      </svg>
    )
  },
  longdash: { 
    strokeDasharray: '16 6', 
    label: 'Длинный пунктир',
    icon: (
      <svg width="20" height="12" viewBox="0 0 20 12" className="mr-2">
        <line x1="2" y1="6" x2="18" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="8 3" />
      </svg>
    )
  }
};



interface EdgeLabelData {
  id: string;
  text: string;
  position: number;
}

const clampLabelPosition = (value: number): number => Math.min(0.95, Math.max(0.05, value));

const getEdgeLabels = (edge: any): EdgeLabelData[] => {
  const labels = edge?.data?.edgeLabels || edge?.data?.wireProperties?.edge_labels || edge?.data?.wireProperties?.edgeLabels;
  return Array.isArray(labels) ? labels : [];
};

const createEdgeLabel = (text: string, index: number): EdgeLabelData => ({
  id: `label-${Date.now()}-${Math.round(Math.random() * 100000)}`,
  text,
  position: clampLabelPosition(0.5 + ((index % 5) - 2) * 0.12),
});

interface SmartLinePanelProps {
  selectedEdge: any;
  selectedEdges?: any[];
  activePopup: string | null;
  setActivePopup: (popup: string | null) => void;
  updateEdgeProperties: (edgeId: string, updates: any) => void;
  onClose: () => void;
  onDeleteConnection?: (connectionId: string) => void;
  onEditRoute?: (edge: any) => void;
  isReadOnly?: boolean;
  /** For "show on schemas" modal: project and current schema (home). Only for connection-* edges. */
  projectId?: number;
  currentSchemaId?: number;
  connectionId?: number;
  connectionVisibility?: { schema_id: number; visibility_legacy: boolean; visible_on_schema_ids: number[] };
  onSaveVisibility?: (connectionId: number, payload: { schema_id?: number; visibility_legacy: boolean; visible_on_schema_ids: number[] }) => Promise<void>;
}

const SmartLinePanel: FC<SmartLinePanelProps> = ({
  selectedEdge,
  selectedEdges = [],
  activePopup,
  setActivePopup,
  updateEdgeProperties,
  onClose,
  onDeleteConnection,
  onEditRoute,
  isReadOnly = false,
  projectId,
  currentSchemaId,
  connectionId,
  connectionVisibility,
  onSaveVisibility
}) => {
  const [visibilityModalOpen, setVisibilityModalOpen] = useState(false);
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const [homeSchemaId, setHomeSchemaId] = useState<number | null>(null);
  const [extraSchemaIds, setExtraSchemaIds] = useState<number[]>([]);
  const [lineColorAdvancedOpen, setLineColorAdvancedOpen] = useState(false);

  useEffect(() => {
    if (activePopup !== 'color') setLineColorAdvancedOpen(false);
  }, [activePopup]);

  const { data: projectSchemas = [] } = useQuery({
    queryKey: ['schemas', projectId],
    queryFn: () => getSchemas(projectId!),
    enabled: visibilityModalOpen && !!projectId
  });

  useEffect(() => {
    if (!visibilityModalOpen || !connectionVisibility) return;
    setHomeSchemaId(connectionVisibility.schema_id ?? currentSchemaId ?? null);
    setExtraSchemaIds(connectionVisibility.visible_on_schema_ids ?? []);
  }, [visibilityModalOpen, connectionVisibility?.schema_id, connectionVisibility?.visible_on_schema_ids, currentSchemaId]);

  if (!selectedEdge && selectedEdges.length === 0) return null;
  
  const edgesToWorkWith = selectedEdges.length > 0 ? selectedEdges : [selectedEdge];
  const primaryEdge = selectedEdge || selectedEdges[0];
  const isConnectionEdge = primaryEdge?.id?.startsWith('connection-') || primaryEdge?.id?.startsWith('device-terminal-');
  const connectionIdFromEdge = isConnectionEdge ? parseInt(primaryEdge.id.replace(/^(connection-|device-terminal-)/, ''), 10) : NaN;
  const effectiveConnectionId = connectionId != null ? connectionId : (!isNaN(connectionIdFromEdge) ? connectionIdFromEdge : undefined);
  const effectiveConnectionVisibility = connectionVisibility ?? (effectiveConnectionId != null && currentSchemaId != null ? { schema_id: currentSchemaId, visibility_legacy: true, visible_on_schema_ids: [] } : undefined);
  const showVisibilityButton = effectiveConnectionId != null && projectId != null && (effectiveConnectionVisibility != null || currentSchemaId != null);
  const edgeLabels = getEdgeLabels(primaryEdge);
  const edgeLabelText = edgeLabels[0]?.text ?? '';
  const updatePrimaryEdgeLabels = (labels: EdgeLabelData[]) => {
    updateEdgeProperties(primaryEdge.id, { edgeLabels: labels });
  };

  useEffect(() => {
    if (!visibilityModalOpen || !effectiveConnectionVisibility) return;
    setHomeSchemaId(effectiveConnectionVisibility.schema_id ?? currentSchemaId ?? null);
    setExtraSchemaIds(effectiveConnectionVisibility.visible_on_schema_ids ?? []);
  }, [visibilityModalOpen, effectiveConnectionVisibility?.schema_id, effectiveConnectionVisibility?.visible_on_schema_ids, currentSchemaId]);

  return createPortal(
    <>
      {/* Main Smart Line Toolbar Panel */}
      <div className="fixed bottom-2 left-2 right-2 md:bottom-4 md:left-1/2 md:right-auto md:transform md:-translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-xl p-2 md:p-4 z-[9999] overflow-x-auto">
        <div className="flex items-center space-x-1 md:space-x-4 justify-center md:justify-start">

          {/* Color Tool */}
          <button
            onClick={() => setActivePopup(activePopup === 'color' ? null : 'color')}
            className="flex items-center space-x-1 px-2 md:px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors text-sm"
            title="Изменить цвет линии"
          >
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: primaryEdge.style?.stroke || '#3b82f6' }}></div>
            <span className="hidden md:inline">Цвет</span>
          </button>

          {/* Type Tool */}
          <button
            onClick={() => setActivePopup(activePopup === 'type' ? null : 'type')}
            className="px-2 md:px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors text-sm flex items-center"
            title="Изменить тип соединения"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" className="mr-1 md:mr-2">
              <path 
                d="M2 14 L8 14 L8 8 L14 8" 
                stroke="currentColor" 
                strokeWidth="2" 
                fill="none" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
            <span className="hidden md:inline">Тип</span>
          </button>

          {/* Style Tool */}
          <button
            onClick={() => setActivePopup(activePopup === 'style' ? null : 'style')}
            className="px-2 md:px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors text-sm"
            title="Изменить стиль линии"
          >
            📏 <span className="hidden md:inline">Стиль</span>
          </button>

          {/* Width Tool */}
          <button
            onClick={() => setActivePopup(activePopup === 'width' ? null : 'width')}
            className="px-2 md:px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors text-sm flex items-center"
            title="Изменить толщину линии"
          >
            <svg width="16" height="12" viewBox="0 0 16 12" className="mr-1 md:mr-2">
              <line x1="0" y1="2" x2="16" y2="2" stroke="currentColor" strokeWidth="1" />
              <line x1="0" y1="6" x2="16" y2="6" stroke="currentColor" strokeWidth="2" />
              <line x1="0" y1="10" x2="16" y2="10" stroke="currentColor" strokeWidth="3" />
            </svg>
            <span className="hidden md:inline">Толщина</span>
          </button>

          {/* Edge Labels Tool */}
          <button
            onClick={() => {
              if (edgeLabels.length === 0) {
                updatePrimaryEdgeLabels([createEdgeLabel('Цепь', 0)]);
              }
              setActivePopup(activePopup === 'labels' ? null : 'labels');
            }}
            className="px-2 md:px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md transition-colors text-sm"
            title={edgeLabels.length === 0 ? 'Создать метку на линии' : 'Редактировать метки линии'}
          >
            🏷️ <span className="hidden md:inline">Метки</span>
          </button>

          {/* Auto Route Tool - Reset waypoints */}
          <button
            onClick={() => {
              // Reset to automatic routing
              const connectionId = parseInt(primaryEdge.id.replace(/^(connection-|auto-terminal-|device-terminal-)/, ''));
              if (!isNaN(connectionId)) {
                // Call onEditRoute with resetToAutomatic flag
                if (onEditRoute) {
                  onEditRoute({ ...primaryEdge, resetToAutomatic: true });
                }
              }
            }}
            className="px-2 md:px-3 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-md transition-colors text-sm"
            title="Сбросить маршрут к автоматическому"
          >
            🔄 <span className="hidden md:inline">Сбросить маршрут</span>
          </button>

          {/* Reset Visuals Tool */}
          <button
            onClick={() => {
              // Reset visual properties (type, style, color, width)
              edgesToWorkWith.forEach(edge => {
                updateEdgeProperties(edge.id, {
                  type: undefined,
                  algorithm: undefined,
                  style: undefined,
                  edgeLabels: undefined
                });
              });
            }}
            className="px-2 md:px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors text-sm"
            title="Сбросить тип/стиль/цвет/толщину"
          >
            🧹 <span className="hidden md:inline">Сбросить стиль</span>
          </button>


          {/* Показывать на схемах — только для connection-* */}
          {showVisibilityButton && (
            <button
              onClick={() => {
                setActivePopup(null);
                setVisibilityModalOpen(true);
              }}
              className="px-2 md:px-3 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-md transition-colors text-sm"
              title="На каких схемах показывать это соединение"
            >
              📋 <span className="hidden md:inline">На схемах</span>
            </button>
          )}

          {/* Delete Tool - скрыт в режиме только просмотр */}
          {!isReadOnly && (
            <button
              onClick={() => {
                if (onDeleteConnection && primaryEdge?.id) {
                  onDeleteConnection(primaryEdge.id);
                } else {
                  onClose();
                }
              }}
              className="px-2 md:px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-md transition-colors text-sm"
              title="Удалить соединение"
            >
              🗑️
            </button>
          )}

          {/* Close Tool */}
          <button
            onClick={onClose}
            className="px-2 md:px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors text-sm"
            title="Закрыть панель"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Color Popup */}
      {activePopup === 'color' && (
        <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-xl p-3 z-[9999] w-[min(96vw,300px)] max-h-[min(78vh,520px)] overflow-y-auto text-left">
          <div className="flex flex-col gap-2">
            <button
              type="button"
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                edgesToWorkWith.forEach((edge) => {
                  updateEdgeProperties(edge.id, {
                    style: { stroke: DEFAULT_LINE_STROKE_HEX },
                  });
                });
              }}
            >
              <Droplets className="h-4 w-4 shrink-0 text-gray-400" aria-hidden strokeWidth={1.75} />
              Сбросить цвет линии
            </button>
            <Circle
              colors={LINE_SHEET_PALETTE}
              color={hsvaToHex(normalizeCssStrokeToHsva(primaryEdge.style?.stroke))}
              style={{ gap: 4, width: '100%', maxWidth: 276 }}
              pointProps={{
                style: { width: 20, height: 20, marginRight: 4, marginBottom: 4 },
              }}
              onChange={(c) => {
                edgesToWorkWith.forEach((edge) => {
                  updateEdgeProperties(edge.id, {
                    style: { stroke: hsvaToRgbaString(c.hsva) },
                  });
                });
              }}
            />
            <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-gray-500">
              Стандартная
            </div>
            <Circle
              colors={LINE_STANDARD_SWATCHES}
              color={hsvaToHex(normalizeCssStrokeToHsva(primaryEdge.style?.stroke))}
              style={{ gap: 6, width: '100%', maxWidth: 276 }}
              pointProps={{
                style: { width: 26, height: 26, marginRight: 6, marginBottom: 0 },
              }}
              onChange={(c) => {
                edgesToWorkWith.forEach((edge) => {
                  updateEdgeProperties(edge.id, {
                    style: { stroke: hsvaToRgbaString(c.hsva) },
                  });
                });
              }}
            />
            <div className="border-t border-gray-100 pt-2 mt-1">
              <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500 mb-2">
                Другой цвет
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  title="Подобрать по спектру и RGB"
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-lg font-semibold leading-none ${
                    lineColorAdvancedOpen
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => setLineColorAdvancedOpen((o) => !o)}
                >
                  +
                </button>
                {getEyeDropperCtor() ? (
                  <button
                    type="button"
                    title="Пипетка с экрана"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100"
                    onClick={async () => {
                      const Eye = getEyeDropperCtor();
                      if (!Eye) return;
                      try {
                        const res = await new Eye().open();
                        edgesToWorkWith.forEach((edge) => {
                          updateEdgeProperties(edge.id, {
                            style: { stroke: hsvaToRgbaString(hexToHsva(res.sRGBHex)) },
                          });
                        });
                      } catch {
                        /* отмена */
                      }
                    }}
                  >
                    <Pipette className="h-4 w-4" aria-hidden strokeWidth={1.75} />
                  </button>
                ) : null}
              </div>
              {lineColorAdvancedOpen ? (
                <div className="mt-3 flex justify-center">
                  <Sketch
                    width={274}
                    color={normalizeCssStrokeToHsva(primaryEdge.style?.stroke)}
                    presetColors={[]}
                    disableAlpha={false}
                    onChange={(c) => {
                      edgesToWorkWith.forEach((edge) => {
                        updateEdgeProperties(edge.id, {
                          style: { stroke: hsvaToRgbaString(c.hsva) },
                        });
                      });
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Type Popup */}
      {activePopup === 'type' && (
        <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-xl p-3 z-[9999] w-[min(95vw,22rem)]">
          <span className="mb-2 block text-xs font-medium text-gray-600">Тип линии</span>
          <div className="flex flex-col gap-1">
            {LINE_ALGORITHM_OPTIONS.map((opt) => {
              const current = primaryEdge.data?.algorithm ?? Algorithm.BezierCatmullRom;
              const active = current === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    edgesToWorkWith.forEach((edge) => {
                      updateEdgeProperties(edge.id, { algorithm: opt.value });
                    });
                    setActivePopup(null);
                  }}
                  className={`rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                    active
                      ? 'border-blue-500 bg-blue-50 font-medium text-blue-900 ring-2 ring-blue-300'
                      : 'border-transparent bg-gray-50 text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Style Popup */}
      {activePopup === 'style' && (
        <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-xl p-4 z-[9999] min-w-[250px] space-y-3">
          <div className="space-y-1">
            <span className="block text-xs font-medium text-gray-600">Стиль</span>
            <select
              value={
                Object.values(LINE_STYLES).find(s => s.strokeDasharray === (primaryEdge.style?.strokeDasharray || '')) ? 
                Object.keys(LINE_STYLES).find(k => LINE_STYLES[k as keyof typeof LINE_STYLES].strokeDasharray === (primaryEdge.style?.strokeDasharray || '')) : 
                'custom'
              }
              onChange={(event) => {
                const preset = event.target.value as keyof typeof LINE_STYLES | 'custom';
                if (preset !== 'custom') {
                  const style = LINE_STYLES[preset];
                  edgesToWorkWith.forEach(edge => {
                    updateEdgeProperties(edge.id, {
                      style: { 
                        strokeDasharray: style.strokeDasharray === '' ? undefined : style.strokeDasharray
                      }
                    });
                  });
                }
              }}
              className="h-9 w-full rounded border border-gray-300 bg-white px-2 text-sm"
            >
              {Object.entries(LINE_STYLES).map(([key, style]) => (
                <option key={key} value={key}>{style.label}</option>
              ))}
              <option value="custom">Свой пунктир</option>
            </select>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <span className="block text-xs font-medium text-gray-600">Штрих</span>
              <input
                type="number"
                min={1}
                max={80}
                value={(() => {
                  const dasharray = primaryEdge.style?.strokeDasharray;
                  if (typeof dasharray !== 'string') return 8;
                  const [dash] = dasharray.split(/\s+/).map(Number);
                  return Number.isFinite(dash) && dash > 0 ? dash : 8;
                })()}
                disabled={!primaryEdge.style?.strokeDasharray}
                onChange={(event) => {
                  const dasharray = primaryEdge.style?.strokeDasharray;
                  let gap = 4;
                  if (typeof dasharray === 'string') {
                    const parts = dasharray.split(/\s+/).map(Number);
                    if (parts.length > 1 && Number.isFinite(parts[1]) && parts[1] > 0) {
                      gap = parts[1];
                    }
                  }
                  const dash = Math.max(1, Number(event.target.value) || 8);
                  edgesToWorkWith.forEach(edge => {
                    updateEdgeProperties(edge.id, {
                      style: { strokeDasharray: `${dash} ${gap}` }
                    });
                  });
                }}
                className="h-9 w-full rounded border border-gray-300 px-2 text-sm disabled:bg-gray-100 disabled:text-gray-400"
              />
            </div>
            <div className="space-y-1">
              <span className="block text-xs font-medium text-gray-600">Пробел</span>
              <input
                type="number"
                min={1}
                max={80}
                value={(() => {
                  const dasharray = primaryEdge.style?.strokeDasharray;
                  if (typeof dasharray !== 'string') return 4;
                  const parts = dasharray.split(/\s+/).map(Number);
                  return parts.length > 1 && Number.isFinite(parts[1]) && parts[1] > 0 ? parts[1] : 4;
                })()}
                disabled={!primaryEdge.style?.strokeDasharray}
                onChange={(event) => {
                  const dasharray = primaryEdge.style?.strokeDasharray;
                  let dash = 8;
                  if (typeof dasharray === 'string') {
                    const parts = dasharray.split(/\s+/).map(Number);
                    if (parts.length > 0 && Number.isFinite(parts[0]) && parts[0] > 0) {
                      dash = parts[0];
                    }
                  }
                  const gap = Math.max(1, Number(event.target.value) || 4);
                  edgesToWorkWith.forEach(edge => {
                    updateEdgeProperties(edge.id, {
                      style: { strokeDasharray: `${dash} ${gap}` }
                    });
                  });
                }}
                className="h-9 w-full rounded border border-gray-300 px-2 text-sm disabled:bg-gray-100 disabled:text-gray-400"
              />
            </div>
          </div>
        </div>
      )}

      {/* Width Popup */}
      {activePopup === 'width' && (
        <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-xl p-4 z-[9999] min-w-[200px]">
          <div className="space-y-1">
            <span className="block text-xs font-medium text-gray-600">Толщина</span>
            <input
              type="number"
              min={1}
              max={24}
              step={1}
              value={Number(primaryEdge.style?.strokeWidth ?? 2) || 2}
              onChange={(event) => {
                const width = Math.max(1, Number(event.target.value) || 2);
                edgesToWorkWith.forEach(edge => {
                  updateEdgeProperties(edge.id, {
                    style: { strokeWidth: width }
                  });
                });
              }}
              className="h-9 w-full rounded border border-gray-300 px-2 text-sm"
            />
          </div>
        </div>
      )}

      {/* Edge Labels Popup */}
      {activePopup === 'labels' && (
        <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-xl p-4 z-[9999] w-80">
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Текст меток</label>
              <input
                type="text"
                value={edgeLabelText}
                placeholder="Например: Цепь 12"
                onChange={(event) => {
                  const text = event.target.value;
                  const labels = edgeLabels.length > 0 ? edgeLabels : [createEdgeLabel(text, 0)];
                  updatePrimaryEdgeLabels(labels.map(label => ({ ...label, text })));
                }}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoFocus
              />
              <p className="text-[11px] text-gray-500 mt-1">Все метки этой линии используют один текст.</p>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const text = edgeLabelText || 'Цепь';
                  const nextLabels = edgeLabels.length > 0 ? edgeLabels : [];
                  updatePrimaryEdgeLabels([...nextLabels, createEdgeLabel(text, nextLabels.length)]);
                }}
                className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
              >
                {edgeLabels.length === 0 ? 'Создать' : 'Добавить дубль'}
              </button>
              {edgeLabels.length > 0 && (
                <button
                  type="button"
                  onClick={() => updatePrimaryEdgeLabels([])}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm"
                >
                  Очистить
                </button>
              )}
            </div>

            {edgeLabels.length > 0 && (
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {edgeLabels.map((label, index) => (
                  <div key={label.id} className="border border-gray-200 rounded-md p-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-700">Метка {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => updatePrimaryEdgeLabels(edgeLabels.filter(item => item.id !== label.id))}
                        className="text-xs text-red-600 hover:text-red-700"
                      >
                        удалить
                      </button>
                    </div>
                    <input
                      type="range"
                      min="5"
                      max="95"
                      value={Math.round((label.position ?? 0.5) * 100)}
                      onChange={(event) => {
                        const position = clampLabelPosition(Number(event.target.value) / 100);
                        updatePrimaryEdgeLabels(edgeLabels.map(item => (
                          item.id === label.id ? { ...item, position } : item
                        )));
                      }}
                      className="w-full"
                    />
                    <div className="flex justify-between text-[10px] text-gray-400">
                      <span>к началу</span>
                      <span>к концу</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Модалка: дом линии и на каких схемах показывать */}
      {visibilityModalOpen && projectId != null && effectiveConnectionId != null && effectiveConnectionVisibility != null && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50" onClick={() => setVisibilityModalOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl p-4 max-w-md w-full mx-2 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-medium mb-2">Показывать соединение на схемах</h3>
            <p className="text-sm text-gray-500 mb-1">Соединение ID: <strong className="font-mono">{effectiveConnectionId}</strong></p>
            <p className="text-sm text-gray-600 mb-3">Дом — схема, к которой «привязана» линия. Линия отображается на схеме-доме и при необходимости на дополнительных схемах.</p>
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2">Дом линии</div>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {projectSchemas.map((schema: { id: number; name?: string }) => {
                  const isSelected = (homeSchemaId ?? effectiveConnectionVisibility.schema_id) === schema.id;
                  return (
                    <label key={schema.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="homeSchema"
                        checked={isSelected}
                        onChange={() => setHomeSchemaId(schema.id)}
                        className="border-gray-300"
                      />
                      <span className={isSelected ? 'font-medium' : ''}>
                        {schema.name || `Схема ${schema.id}`}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2">Показывать также на</div>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {projectSchemas
                  .filter((schema: { id: number }) => schema.id !== (homeSchemaId ?? effectiveConnectionVisibility.schema_id))
                  .map((schema: { id: number; name?: string }) => {
                    const checked = extraSchemaIds.includes(schema.id);
                    return (
                      <label key={schema.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setExtraSchemaIds(prev =>
                              prev.includes(schema.id)
                                ? prev.filter(id => id !== schema.id)
                                : [...prev, schema.id]
                            );
                          }}
                          className="rounded border-gray-300"
                        />
                        <span>{schema.name || `Схема ${schema.id}`}</span>
                      </label>
                    );
                  })}
                {projectSchemas.filter((s: { id: number }) => s.id !== (homeSchemaId ?? effectiveConnectionVisibility.schema_id)).length === 0 && (
                  <span className="text-sm text-gray-500">Нет других схем в проекте</span>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setVisibilityModalOpen(false)}
                className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 rounded text-sm"
              >
                Отмена
              </button>
              <button
                type="button"
                disabled={visibilitySaving}
                onClick={async () => {
                  setVisibilitySaving(true);
                  const effectiveHome = homeSchemaId ?? effectiveConnectionVisibility.schema_id;
                  const payload = { schema_id: effectiveHome, visibility_legacy: false, visible_on_schema_ids: extraSchemaIds };
                  try {
                    if (onSaveVisibility) {
                      await onSaveVisibility(effectiveConnectionId, payload);
                    } else {
                      await updateConnection(effectiveConnectionId, payload);
                    }
                    setVisibilityModalOpen(false);
                  } finally {
                    setVisibilitySaving(false);
                  }
                }}
                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-sm disabled:opacity-50"
              >
                {visibilitySaving ? 'Сохранение…' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default SmartLinePanel; 